import { useEffect, useState, useRef } from "react";
import { API } from "../lib/constants";
import { useAdmin } from "../contexts/AdminContext";
import { Database, GitBranch, UploadCloud, RefreshCw, AlertTriangle, Loader2, FileAudio, Sparkles, Download, FileText, Pause, Wand2, Image as ImageIcon } from "lucide-react";

const fmtPct = (n, d) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : "0%");

// Admin token is kept in localStorage by AdminContext — must be sent on every
// admin call as a bearer header. `credentials: "include"` alone won't authenticate.
const adminHeaders = () => {
  const t = localStorage.getItem("admin_token") || "";
  return t ? { Authorization: `Bearer ${t}`, "X-Admin-Token": t } : {};
};

export default function FileRecovery() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreLog, setRestoreLog] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [overwrite, setOverwrite] = useState(false);
  const [inspectResult, setInspectResult] = useState(null);
  const [relinkRunning, setRelinkRunning] = useState(false);
  const [relinkResult, setRelinkResult] = useState(null);
  const [seriesProgress, setSeriesProgress] = useState([]);
  const [ttsJobs, setTtsJobs] = useState([]);
  const [regenStarting, setRegenStarting] = useState(false);
  const [errorsPanel, setErrorsPanel] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pruning, setPruning] = useState(false);
  const [missingAudio, setMissingAudio] = useState(null);
  const [healing, setHealing] = useState(false);
  const [imgCoverage, setImgCoverage] = useState(null);
  const [charImgJobs, setCharImgJobs] = useState([]);
  const [castImgStarting, setCastImgStarting] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const fileInputRef = useRef(null);
  const { logout } = useAdmin();

  const refresh = async () => {
    setRefreshing(true);
    let sawAuthFail = false;
    // Independent fetches so one failure doesn't kill the whole panel.
    const safe = (url) =>
      fetch(`${API}${url}`, { headers: adminHeaders(), credentials: "include" })
        .then(r => {
          if (r.status === 401 || r.status === 403) { sawAuthFail = true; return null; }
          return r.ok ? r.json() : null;
        })
        .catch(() => null);

    const [r, sp, jb, errs, ma, ic, cij] = await Promise.all([
      safe(`/admin/recovery-status`),
      safe(`/admin/series-progress`),
      safe(`/tts/batch/status`),
      safe(`/tts/batch/errors`),
      safe(`/admin/missing-audio`),
      safe(`/character-images/coverage`),
      safe(`/character-images/batch-status`),
    ]);
    if (r) setStatus(r);
    if (sp) setSeriesProgress(sp.series || []);
    if (jb) setTtsJobs(jb);
    if (errs) setErrorsPanel(errs);
    if (ma) setMissingAudio(ma);
    if (ic) setImgCoverage(ic);
    if (cij) setCharImgJobs(cij);
    setAuthExpired(sawAuthFail);
    setLastRefreshAt(new Date());
    setRefreshing(false);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 8000);
    return () => clearInterval(iv);
  }, []);

  const startGitRestore = async () => {
    setRestoreRunning(true);
    setRestoreLog(null);
    try {
      const r = await fetch(`${API}/admin/restore-from-git`, {
        method: "POST", headers: adminHeaders(), credentials: "include",
      });
      const data = await r.json();
      if (data.status === "started" || data.status === "already_running") {
        pollRestore();
      } else {
        setRestoreRunning(false);
      }
    } catch (e) {
      setRestoreRunning(false);
      setRestoreLog({ error: String(e) });
    }
  };

  const pollRestore = () => {
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`${API}/admin/restore-from-git/status`, { headers: adminHeaders(), credentials: "include" });
        if (r.ok) {
          const job = await r.json();
          setRestoreLog(job);
          if (!job.running) {
            clearInterval(iv);
            setRestoreRunning(false);
            refresh();
          }
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
  };

  // Chunk a large file list into batches so the request body stays manageable.
  const BATCH_SIZE = 20;

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList).filter(f =>
      /\.(mp3|wav|ogg|png|jpe?g|webp)$/i.test(f.name)
    );
    if (!files.length) return;

    setUploading(true);
    setUploadResult(null);
    setUploadProgress({ done: 0, total: files.length });

    let agg = { received: 0, uploaded: 0, skipped_existing: 0, skipped_unknown: 0, errors: 0, episodes_updated: 0, items: [] };

    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      const fd = new FormData();
      batch.forEach(f => fd.append("files", f, f.name));
      fd.append("overwrite", overwrite ? "true" : "false");
      fd.append("update_episodes", "true");

      try {
        const r = await fetch(`${API}/admin/bulk-upload-audio`, {
          method: "POST", headers: adminHeaders(), credentials: "include", body: fd,
        });
        if (r.ok) {
          const data = await r.json();
          agg.received += data.received || 0;
          agg.uploaded += data.uploaded || 0;
          agg.skipped_existing += data.skipped_existing || 0;
          agg.skipped_unknown += data.skipped_unknown || 0;
          agg.errors += data.errors || 0;
          agg.episodes_updated += data.episodes_updated || 0;
          if (data.items) agg.items = agg.items.concat(data.items.slice(0, 5));
        } else {
          // Surface the server's failure so the user knows what's wrong instead
          // of seeing a silent 269/269 "errors" count.
          let detail = `HTTP ${r.status}`;
          try { const e = await r.json(); if (e.detail) detail = e.detail; } catch (_) {}
          agg.errors += batch.length;
          agg.last_error = detail;
        }
      } catch (e) {
        agg.errors += batch.length;
      }
      setUploadProgress({ done: Math.min(i + BATCH_SIZE, files.length), total: files.length });
      setUploadResult({ ...agg });
    }

    setUploading(false);
    refresh();
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => { e.preventDefault(); };

  const inspectGridFS = async () => {
    try {
      const r = await fetch(`${API}/admin/gridfs-inspect?prefix=tts/&limit=20`, { headers: adminHeaders(), credentials: "include" });
      if (r.ok) setInspectResult(await r.json());
    } catch (e) { /* ignore */ }
  };

  const relinkEpisodes = async () => {
    setRelinkRunning(true);
    setRelinkResult(null);
    try {
      const r = await fetch(`${API}/admin/relink-episodes`, {
        method: "POST", headers: adminHeaders(), credentials: "include",
      });
      if (r.ok) {
        const data = await r.json();
        setRelinkResult(data);
        await refresh();
      } else {
        setRelinkResult({ error: `HTTP ${r.status}` });
      }
    } catch (e) {
      setRelinkResult({ error: String(e) });
    } finally {
      setRelinkRunning(false);
    }
  };

  const regenSeries = async (slug) => {
    if (!window.confirm(`Start TTS regeneration for "${slug}"?\n\nThis will spend real OpenAI credits (≈ $0.10/episode, ~$40 per series). Already-existing audio is skipped.`)) return;
    setRegenStarting(true);
    try {
      const r = await fetch(`${API}/tts/batch`, {
        method: "POST",
        headers: { ...adminHeaders(), "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ series: slug, skip_existing: true }),
      });
      const data = await r.json();
      if (r.ok) {
        await refresh();
        alert(`Started: ${data.message || "OK"}\n\nKeep this page open to watch progress.`);
      } else {
        alert(`Failed: ${data.detail || `HTTP ${r.status}`}`);
      }
    } catch (e) {
      alert(`Network error: ${e}`);
    } finally {
      setRegenStarting(false);
    }
  };

  const cancelTtsJob = async (job_id) => {
    try {
      await fetch(`${API}/tts/batch/${job_id}/cancel`, {
        method: "POST", headers: adminHeaders(), credentials: "include",
      });
      refresh();
    } catch (e) { /* ignore */ }
  };

  const downloadManifest = () => {
    // Use XMLHttpRequest, NOT fetch — Emergent's monitoring layer breaks fetch
    // by trying to postMessage-clone the Request object, which throws
    // DataCloneError. Same workaround used by httpClient.js for auth.
    const token = localStorage.getItem("admin_token") || "";
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${API}/admin/manifest.csv`, true);
    xhr.responseType = "blob";
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("X-Admin-Token", token);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const url = URL.createObjectURL(xhr.response);
        const a = document.createElement("a");
        a.href = url;
        a.download = "redink_audio_manifest.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        alert(`Manifest download failed: HTTP ${xhr.status}`);
      }
    };
    xhr.onerror = () => alert("Manifest download failed: network error");
    xhr.send();
  };

  const downloadSeriesZip = (slug) => {
    if (!window.confirm(`Download all generated audio for "${slug}" as a ZIP?\n\nThis may take a few minutes and produce a large file (up to ~1.5 GB).`)) return;
    const token = localStorage.getItem("admin_token") || "";
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${API}/admin/download-series-zip?series=${encodeURIComponent(slug)}`, true);
    xhr.responseType = "blob";
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("X-Admin-Token", token);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const url = URL.createObjectURL(xhr.response);
        const a = document.createElement("a");
        a.href = url;
        a.download = `redink_${slug}_audio.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        alert(`ZIP download failed: HTTP ${xhr.status}`);
      }
    };
    xhr.onerror = () => alert("ZIP download failed: network error");
    xhr.send();
  };

  const selfHeal = async () => {
    const n = missingAudio?.total_broken || 0;
    if (n === 0) return;
    if (!window.confirm(
      `Self-heal ${n} episode(s) with missing audio?\n\n` +
      `This regenerates TTS audio ONLY for episodes whose audio file is missing ` +
      `from GridFS (it skips everything that already plays), then saves each one ` +
      `back to storage. It spends OpenAI credits (~$${(n * 0.10).toFixed(2)} est.) ` +
      `and runs in the background — you can watch progress below.`
    )) return;
    setHealing(true);
    try {
      const r = await fetch(`${API}/admin/self-heal-audio`, {
        method: "POST", headers: adminHeaders(), credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(`Self-heal failed: ${data.detail || `HTTP ${r.status}`}`);
        return;
      }
      alert(data.message || `Self-heal started for ${data.count} episode(s).`);
      await refresh();
    } catch (e) {
      alert(`Self-heal failed: ${e}`);
    } finally {
      setHealing(false);
    }
  };

  const generateCastImages = async () => {
    const n = imgCoverage?.missing || 0;
    if (n === 0) return;
    if (!window.confirm(
      `Generate portraits for ${n} character(s) missing an image?\n\n` +
      `This uses gpt-image-1 (~$0.02–0.19 each) and saves every portrait to ` +
      `GridFS so it survives future restarts. It only generates the missing ` +
      `ones and runs in the background — watch progress below.`
    )) return;
    setCastImgStarting(true);
    try {
      const r = await fetch(`${API}/character-images/batch-generate`, {
        method: "POST",
        headers: { ...adminHeaders(), "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ only_missing: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(`Failed: ${data.detail || `HTTP ${r.status}`}`);
        return;
      }
      alert(data.message || `Started generating ${data.total} portrait(s).`);
      await refresh();
    } catch (e) {
      alert(`Failed: ${e}`);
    } finally {
      setCastImgStarting(false);
    }
  };

  const pruneBrokenUrls = async () => {    if (!window.confirm(
      "Clean broken audio_url values?\n\n" +
      "This scans every episode and clears audio_url / mixed_audio_url that " +
      "point at files NOT present in GridFS. Safe to run anytime — re-runs " +
      "are no-ops once the database is clean. Episodes with real audio are " +
      "unaffected."
    )) return;
    setPruning(true);
    try {
      const r = await fetch(`${API}/admin/prune-broken-audio-urls`, {
        method: "POST", headers: adminHeaders(), credentials: "include",
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        alert(`Prune failed: ${e.detail || `HTTP ${r.status}`}`);
        return;
      }
      const data = await r.json();
      alert(
        `Cleaned ${data.broken_episodes_pruned} broken audio_url entries.\n\n` +
        `The dashboard will now reflect the real number of playable episodes ` +
        `(file present in GridFS).`
      );
      await refresh();
    } catch (e) {
      alert(`Prune failed: ${e}`);
    } finally {
      setPruning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-red-600" />
      </div>
    );
  }

  const eps = status?.episodes || { total: 0, with_audio_url_in_db: 0, actually_playable_now: 0 };
  const gridfs = status?.gridfs || { total_files: 0, by_category: {} };
  const gap = eps.total - eps.actually_playable_now;
  const job = restoreLog || status?.restore_job;

  return (
    <div className="min-h-screen pt-24 pb-32 px-6" data-testid="file-recovery-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-red-500/70 text-xs tracking-widest mb-2">ADMIN · FILE RECOVERY</p>
          <h1 className="font-cinzel text-3xl md:text-4xl font-bold mb-2">Rebuild GridFS</h1>
          <p className="text-sm text-gray-500 max-w-2xl">
            One-time recovery tools to repopulate MongoDB GridFS after the May 2026 filesystem wipe.
            New audio generated from this point forward is persisted automatically.
          </p>
        </div>

        {authExpired && (
          <div
            className="rounded-xl border border-red-500/50 bg-red-500/10 p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="admin-session-expired-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-red-300">
                Your admin session has expired, so the recovery tools below can't load their data.
                Re-enter the admin password to continue.
              </p>
            </div>
            <button
              onClick={logout}
              data-testid="admin-relogin-btn"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
            >
              Re-enter Password
            </button>
          </div>
        )}

        {/* Status cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <StatCard
            icon={<Database size={18} />}
            label="Episodes playable now"
            value={`${eps.actually_playable_now} / ${eps.total}`}
            sub={fmtPct(eps.actually_playable_now, eps.total)}
            tone={gap === 0 ? "ok" : gap > 100 ? "warn" : "info"}
            testid="stat-playable"
          />
          <StatCard
            icon={<FileAudio size={18} />}
            label="Files in GridFS"
            value={gridfs.total_files.toLocaleString()}
            sub={Object.entries(gridfs.by_category).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            tone="info"
            testid="stat-gridfs"
          />
          <StatCard
            icon={<AlertTriangle size={18} />}
            label="Missing audio"
            value={gap}
            sub={`${eps.with_audio_url_in_db} episodes have URLs in DB`}
            tone={gap === 0 ? "ok" : "warn"}
            testid="stat-missing"
          />
        </div>

        {/* Self-Heal — one-click fix for episodes whose audio file is missing */}
        {(() => {
          const broken = missingAudio?.total_broken ?? null;
          const bySeries = missingAudio?.by_series || [];
          const allGood = broken === 0;
          const healJob = ttsJobs.find(j => j.series === "Self-heal" && (j.status === "running" || j.status === "queued"));
          const healPct = healJob?.total_episodes ? Math.round((healJob.completed / healJob.total_episodes) * 100) : 0;
          return (
            <div
              className={`rounded-xl border p-5 mb-8 ${allGood ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
              data-testid="self-heal-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 ${allGood ? "text-emerald-500" : "text-amber-500"}`}>
                    <Wand2 size={22} />
                  </div>
                  <div>
                    <h2 className="font-cinzel text-lg font-bold">Self-Heal Missing Audio</h2>
                    {broken === null ? (
                      <p className="text-sm text-gray-500">Scanning for episodes with missing audio…</p>
                    ) : allGood ? (
                      <p className="text-sm text-emerald-600" data-testid="self-heal-allgood">
                        Every episode with a story has working audio. Nothing to heal. 🎉
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500" data-testid="self-heal-summary">
                        <span className="font-semibold text-amber-600">{broken}</span> episode(s) have a missing
                        audio file. One click regenerates <em>only</em> these (skips everything that plays) and
                        saves them to storage.
                        {bySeries.length > 0 && (
                          <span className="block mt-1 text-xs text-gray-400">
                            {bySeries.map((s) => `${s.title}: ${s.count}`).join(" · ")}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                {!allGood && broken !== null && !healJob && (
                  <button
                    onClick={selfHeal}
                    disabled={healing}
                    data-testid="self-heal-btn"
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
                  >
                    {healing ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
                    {healing ? "Starting…" : `Heal ${broken} episode${broken === 1 ? "" : "s"}`}
                  </button>
                )}
                {healJob && (
                  <span className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-400 text-sm font-semibold" data-testid="self-heal-running-chip">
                    <Loader2 size={15} className="animate-spin" /> Healing…
                  </span>
                )}
              </div>
              {healJob && (
                <div className="mt-4 p-4 bg-black/30 border border-white/10 rounded-lg font-mono text-xs space-y-2" data-testid="self-heal-progress">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Healing in progress ({healPct}%)</span>
                    <span className="text-amber-400">
                      {healJob.completed}/{healJob.total_episodes} done{healJob.failed > 0 ? ` · ${healJob.failed} failed` : ""}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all" style={{ width: `${healPct}%` }} />
                  </div>
                  {healJob.current_episode && (
                    <div className="text-gray-400 truncate" data-testid="self-heal-current-ep">Now recording: {healJob.current_episode}</div>
                  )}
                  {healJob.message && <div className="text-gray-500 truncate" data-testid="self-heal-live-msg">{healJob.message}</div>}
                  <div className="text-gray-600">
                    Each episode is fully voiced segment-by-segment (~10–20 min each). It's safe to close this
                    page — healing continues on the server and this panel updates live whenever you return.
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Restore Cast Images — regenerate missing character portraits */}
        {(() => {
          const missing = imgCoverage?.missing ?? null;
          const total = imgCoverage?.total ?? 0;
          const withImage = imgCoverage?.with_image ?? 0;
          const allGood = missing === 0 && total > 0;
          const activeJob = charImgJobs.find((j) => j.status === "running");
          return (
            <div
              className={`rounded-xl border p-5 mb-8 ${allGood ? "border-emerald-500/30 bg-emerald-500/5" : "border-purple-500/40 bg-purple-500/5"}`}
              data-testid="cast-images-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 ${allGood ? "text-emerald-500" : "text-purple-400"}`}>
                    <ImageIcon size={22} />
                  </div>
                  <div>
                    <h2 className="font-cinzel text-lg font-bold">Restore Cast Images</h2>
                    {missing === null ? (
                      <p className="text-sm text-gray-500">Checking character portraits…</p>
                    ) : allGood ? (
                      <p className="text-sm text-emerald-600" data-testid="cast-images-allgood">
                        All {total} characters have a portrait saved in GridFS. 🎉
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500" data-testid="cast-images-summary">
                        <span className="font-semibold text-purple-300">{missing}</span> of {total} characters are
                        missing a portrait ({withImage} present). One click AI-generates <em>only</em> the missing ones
                        with <code>gpt-image-1</code> and saves them to GridFS.
                        {imgCoverage?.missing_by_series?.length > 0 && (
                          <span className="block mt-1 text-xs text-gray-400">
                            {imgCoverage.missing_by_series.map((s) => `${s.series}: ${s.count}`).join(" · ")}
                          </span>
                        )}
                      </p>
                    )}
                    {activeJob && (
                      <div className="mt-2 text-[11px] font-mono text-purple-300" data-testid="cast-images-job">
                        Generating… {activeJob.done}/{activeJob.total} done
                        {activeJob.failed > 0 ? ` · ${activeJob.failed} failed` : ""}
                        {activeJob.current ? ` · ${activeJob.current}` : ""}
                      </div>
                    )}
                    {!activeJob && charImgJobs[0] && charImgJobs[0].status === "done" && (
                      <div className="mt-2 text-[11px] font-mono text-gray-500" data-testid="cast-images-last">
                        Last run: {charImgJobs[0].done} generated
                        {charImgJobs[0].failed > 0 ? `, ${charImgJobs[0].failed} failed (check LLM key balance)` : ""}.
                      </div>
                    )}
                  </div>
                </div>
                {!allGood && missing !== null && (
                  <button
                    onClick={generateCastImages}
                    disabled={castImgStarting || !!activeJob}
                    data-testid="generate-cast-images-btn"
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
                  >
                    {castImgStarting || activeJob ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />}
                    {activeJob ? "Generating…" : castImgStarting ? "Starting…" : `Generate ${missing} portrait${missing === 1 ? "" : "s"}`}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Step 1 — git restore */}
        <Section
          step="1"
          icon={<GitBranch size={20} className="text-red-500" />}
          title="Restore from Git History"
          subtitle="Scans .git/objects for any audio/image blob ever committed and pushes it into GridFS. Safe to re-run — already-present files are skipped."
        >
          <button
            onClick={startGitRestore}
            disabled={restoreRunning || job?.running}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-bold text-white flex items-center gap-2"
            data-testid="start-git-restore-btn"
          >
            {(restoreRunning || job?.running) ? (
              <><Loader2 size={16} className="animate-spin" /> Running…</>
            ) : (
              <><GitBranch size={16} /> Start Git Restore</>
            )}
          </button>

          {job && (
            <div className="mt-4 p-4 bg-black/30 border border-white/10 rounded-lg font-mono text-xs space-y-1.5" data-testid="git-restore-log">
              <div className="flex justify-between"><span className="text-gray-500">Scanned:</span><span className="text-gray-300">{job.scanned}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Uploaded:</span><span className="text-green-400">{job.uploaded}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Skipped (already present):</span><span className="text-gray-400">{job.skipped}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Errors:</span><span className={job.errors > 0 ? "text-amber-400" : "text-gray-400"}>{job.errors}</span></div>
              {job.last_file && <div className="text-gray-600 truncate">last: {job.last_file}</div>}
              <div className="pt-2 border-t border-white/5 text-gray-400">{job.message}</div>
            </div>
          )}
        </Section>

        {/* Step 2 — bulk upload from PC */}
        <Section
          step="2"
          icon={<UploadCloud size={20} className="text-red-500" />}
          title="Upload Audio Files From Your PC"
          subtitle="Drop your NC-1-main folder (or any folder of MP3s) here. Each file's UUID is matched to an episode and the audio_url is wired up automatically."
        >
          <div className="mb-3 flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs font-mono text-gray-400 cursor-pointer select-none" data-testid="overwrite-toggle-label">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                className="rounded border-white/20 bg-black/30"
                data-testid="overwrite-toggle"
              />
              FORCE OVERWRITE — re-upload even if "already present" in GridFS
            </label>
          </div>
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onClick={() => fileInputRef.current?.click()}
            className="cursor-pointer border-2 border-dashed border-white/15 hover:border-red-500/50 rounded-xl py-10 px-6 text-center transition-colors"
            data-testid="bulk-upload-dropzone"
          >
            <UploadCloud size={32} className="mx-auto mb-3 text-gray-500" />
            <p className="text-sm text-gray-300 mb-1">
              {uploading ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : "Click to choose files, or drag & drop here"}
            </p>
            <p className="text-xs text-gray-500">.mp3, .wav, .png, .jpg — bulk upload</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".mp3,.wav,.ogg,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="bulk-upload-input"
            />
          </div>

          {uploadResult && (
            <div className="mt-4 p-4 bg-black/30 border border-white/10 rounded-lg font-mono text-xs space-y-1.5" data-testid="bulk-upload-result">
              <div className="flex justify-between"><span className="text-gray-500">Files received:</span><span className="text-gray-300">{uploadResult.received}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Uploaded to GridFS:</span><span className="text-green-400">{uploadResult.uploaded}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Episodes wired up:</span><span className="text-green-400">{uploadResult.episodes_updated}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Already present:</span><span className="text-gray-400">{uploadResult.skipped_existing}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Unknown format:</span><span className="text-gray-400">{uploadResult.skipped_unknown}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Errors:</span><span className={uploadResult.errors > 0 ? "text-amber-400" : "text-gray-400"}>{uploadResult.errors}</span></div>
              {uploadResult.last_error && (
                <div className="pt-2 border-t border-white/5 text-amber-300 break-words">last error: {uploadResult.last_error}</div>
              )}
              {uploading && <div className="pt-2 border-t border-white/5 text-gray-400">Batch progress: {uploadProgress.done}/{uploadProgress.total}</div>}
            </div>
          )}
        </Section>

        {/* Step 3 — Re-link episodes to existing GridFS files */}
        <Section
          step="3"
          icon={<Database size={20} className="text-red-500" />}
          title="Re-link Episodes to GridFS"
          subtitle="If files are already in GridFS but episodes don't know about them (audio_url is blank), this scans GridFS, matches each file's UUID to an episode, and wires up audio_url automatically. Safe to run anytime."
        >
          <button
            onClick={relinkEpisodes}
            disabled={relinkRunning}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-bold text-white flex items-center gap-2"
            data-testid="relink-episodes-btn"
          >
            {relinkRunning ? (
              <><Loader2 size={16} className="animate-spin" /> Re-linking…</>
            ) : (
              <><Database size={16} /> Re-link Episodes Now</>
            )}
          </button>

          {relinkResult && (
            <div className="mt-4 p-4 bg-black/30 border border-white/10 rounded-lg font-mono text-xs space-y-1.5" data-testid="relink-result">
              {relinkResult.error ? (
                <div className="text-amber-300">{relinkResult.error}</div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-gray-500">Audio files scanned in GridFS:</span><span className="text-gray-300">{relinkResult.gridfs_audio_files}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Episodes wired up (audio_url):</span><span className="text-green-400">{relinkResult.episodes_audio_url_set}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Episodes wired up (mixed):</span><span className="text-green-400">{relinkResult.episodes_mixed_url_set}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Files with no matching episode (orphans):</span><span className={relinkResult.episodes_no_match > 0 ? "text-amber-400" : "text-gray-400"}>{relinkResult.episodes_no_match}</span></div>
                  {relinkResult.orphan_files?.length > 0 && (
                    <details className="pt-2 border-t border-white/5">
                      <summary className="cursor-pointer text-gray-500 hover:text-gray-300">Sample orphan filenames ({relinkResult.orphan_files.length} of {relinkResult.episodes_no_match})</summary>
                      <div className="mt-2 space-y-0.5 max-h-48 overflow-auto">
                        {relinkResult.orphan_files.map(fn => (
                          <div key={fn} className="text-gray-500 truncate">{fn}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          )}
        </Section>

        {/* Step 4 — Regenerate Audio (batch TTS per series) */}
        <Section
          step="4"
          icon={<Sparkles size={20} className="text-red-500" />}
          title="Regenerate Audio (Batch TTS)"
          subtitle={
            <>
              Generate fresh TTS audio for missing episodes using OpenAI <code>tts-1</code>.
              Voices follow each character's assigned voice; pace = 1.0 conversational, narrator 1.05.
              Sentences are never cut mid-stream. Audio is written to GridFS (persistent) the moment each episode finishes.
              <br />
              <strong className="text-amber-300">Cost: ~$0.10 / episode.</strong> Already-generated episodes are skipped.
            </>
          }
        >
          <div className="space-y-2" data-testid="series-progress-list">
            {seriesProgress.length === 0 && (
              <div className="text-xs text-gray-500">Loading series…</div>
            )}
            {seriesProgress.map((s) => {
              const pct = s.total_episodes > 0 ? (s.tts_ready / s.total_episodes) * 100 : 0;
              const missingCost = (s.missing * 0.10).toFixed(2);
              const job = ttsJobs.find(j => j.series === s.slug && (j.status === "running" || j.status === "queued"));
              return (
                <div key={s.slug} className="p-3 rounded-lg border border-white/10 bg-black/30" data-testid={`series-row-${s.slug}`}>
                  <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm text-gray-100 truncate">{s.title || s.slug}</div>
                      <div className="text-[10px] font-mono text-gray-500">
                        {s.tts_ready} / {s.total_episodes} ready · {s.missing} missing · ~${missingCost} to complete
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {job ? (
                        <button
                          onClick={() => cancelTtsJob(job.id)}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded text-xs font-bold text-white flex items-center gap-1.5"
                          data-testid={`cancel-${s.slug}-btn`}
                        >
                          <Pause size={12} /> Cancel job
                        </button>
                      ) : (
                        <button
                          onClick={() => regenSeries(s.slug)}
                          disabled={regenStarting || s.missing === 0}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs font-bold text-white flex items-center gap-1.5"
                          data-testid={`regen-${s.slug}-btn`}
                        >
                          <Sparkles size={12} /> {s.missing === 0 ? "Complete" : `Regenerate (${s.missing})`}
                        </button>
                      )}
                      <button
                        onClick={() => downloadSeriesZip(s.slug)}
                        disabled={s.tts_ready === 0}
                        className="px-3 py-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs font-bold text-gray-200 flex items-center gap-1.5"
                        data-testid={`zip-${s.slug}-btn`}
                        title="Download all generated audio as a ZIP backup"
                      >
                        <Download size={12} /> ZIP
                      </button>
                    </div>
                  </div>

                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-700 to-red-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {job && (
                    <div className="mt-2 text-[10px] font-mono text-gray-400" data-testid={`job-progress-${s.slug}`}>
                      Job <span className="text-amber-300">{job.id}</span> · {job.status} · {job.completed || 0}/{job.total_episodes || "?"} done
                      {job.current_episode ? ` · ${job.current_episode}` : ""}
                      {job.message ? <div className="text-gray-500 truncate">{job.message}</div> : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={downloadManifest}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-xs font-bold text-gray-200 flex items-center gap-2"
              data-testid="manifest-csv-btn"
            >
              <FileText size={14} /> Download manifest (CSV)
            </button>
            <button
              onClick={pruneBrokenUrls}
              disabled={pruning}
              className="px-4 py-2 bg-amber-600/20 hover:bg-amber-600/30 disabled:opacity-50 rounded-lg text-xs font-bold text-amber-200 flex items-center gap-2 border border-amber-500/30"
              data-testid="prune-broken-btn"
              title="Clear audio_url on episodes whose file no longer exists in GridFS"
            >
              {pruning
                ? <><Loader2 size={14} className="animate-spin" /> Cleaning…</>
                : <><AlertTriangle size={14} /> Clean broken URLs</>}
            </button>
            <div className="text-[10px] text-gray-600 max-w-md">
              CSV maps every episode → audio file → GridFS status.
              "Clean broken URLs" syncs the database to reality — runs after a
              failed batch to remove pointers to files that never persisted.
            </div>
          </div>

          {/* Error / Health panel — surfaces background failures so the dashboard
              never lies about "completed" episodes that actually failed to persist. */}
          {errorsPanel && (
            <div className="mt-5 p-4 bg-black/40 border border-amber-500/30 rounded-lg text-xs" data-testid="tts-errors-panel">
              <div className="font-mono uppercase tracking-wider text-amber-300 mb-2 flex items-center gap-2">
                <AlertTriangle size={14} /> Background TTS Health
              </div>
              {errorsPanel.mongo && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-[10px] font-mono">
                  <div>
                    <div className="text-gray-500">GridFS size</div>
                    <div className="text-gray-200">{errorsPanel.mongo.gridfs_size_mb != null ? `${errorsPanel.mongo.gridfs_size_mb} MB` : "?"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">GridFS files</div>
                    <div className="text-gray-200">{errorsPanel.mongo.fs_files_count ?? "?"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Mongo</div>
                    <div className={errorsPanel.mongo.mongo_ok ? "text-green-400" : "text-amber-400"}>
                      {errorsPanel.mongo.mongo_ok ? "OK" : "DOWN"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">DB storage</div>
                    <div className="text-gray-200">{errorsPanel.mongo.storage_size_mb != null ? `${errorsPanel.mongo.storage_size_mb} MB` : "—"}</div>
                  </div>
                </div>
              )}
              {errorsPanel.error_count > 0 ? (
                <details open>
                  <summary className="cursor-pointer text-amber-200 hover:text-amber-100">
                    {errorsPanel.error_count} recent error{errorsPanel.error_count === 1 ? "" : "s"} — click to view
                  </summary>
                  <div className="mt-2 space-y-0.5 max-h-60 overflow-auto font-mono text-[10px]" data-testid="tts-errors-list">
                    {errorsPanel.errors.map((line, i) => (
                      <div key={i} className="text-gray-400 break-words border-l-2 border-amber-500/40 pl-2">{line}</div>
                    ))}
                  </div>
                </details>
              ) : (
                <div className="text-gray-500">No errors recorded in this session.</div>
              )}
            </div>
          )}
        </Section>

        <div className="text-center text-xs text-gray-600 mt-8 flex items-center justify-center gap-3 flex-wrap">
          <span>
            Status auto-refreshes every 8 s
            {lastRefreshAt && (
              <span className="text-gray-500 ml-1" data-testid="last-refresh-ts">
                · last: {lastRefreshAt.toLocaleTimeString()}
              </span>
            )}
          </span>
          <span>·</span>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="text-gray-400 hover:text-gray-200 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="refresh-status-btn"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> refresh now
          </button>
          <span>·</span>
          <button onClick={inspectGridFS} className="text-gray-400 hover:text-gray-200 inline-flex items-center gap-1" data-testid="inspect-gridfs-btn">
            <Database size={12} /> what's actually in GridFS?
          </button>
        </div>

        {inspectResult && (
          <div className="mt-4 p-4 bg-black/50 border border-white/10 rounded-lg font-mono text-xs" data-testid="inspect-result">
            <div className="mb-2 text-gray-400">
              GridFS contains <span className="text-amber-300">{inspectResult.all_files_count}</span> files total.
              {' '}<span className="text-amber-300">{inspectResult.total_matching}</span> match prefix "{inspectResult.prefix}".
            </div>
            {inspectResult.sample.length > 0 ? (
              <div className="space-y-0.5 max-h-60 overflow-auto">
                {inspectResult.sample.map((s) => (
                  <div key={s.filename} className="text-gray-500">
                    <span className="text-gray-300">{s.filename}</span>
                    <span className="ml-2 text-gray-600">{(s.length / 1024).toFixed(1)} KB</span>
                  </div>
                ))}
                {inspectResult.total_matching > inspectResult.sample.length && (
                  <div className="text-gray-600 pt-1">…and {inspectResult.total_matching - inspectResult.sample.length} more</div>
                )}
              </div>
            ) : (
              <div className="text-gray-500">No files match this prefix in GridFS.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone = "info", testid }) {
  const toneColor = {
    ok: "border-green-500/30 bg-green-500/5 text-green-400",
    warn: "border-amber-500/30 bg-amber-500/5 text-amber-400",
    info: "border-white/10 bg-[#0c0c0c] text-gray-300",
  }[tone];
  return (
    <div className={`p-4 rounded-xl border ${toneColor}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider opacity-70">
        {icon}{label}
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {sub && <div className="mt-1 text-[11px] opacity-60 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function Section({ step, icon, title, subtitle, children }) {
  return (
    <section className="mb-6 p-5 rounded-xl border border-white/10 bg-[#0c0c0c]">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-red-600/15 border border-red-500/30 text-red-400 font-bold flex items-center justify-center flex-shrink-0">{step}</div>
        <div>
          <h2 className="font-cinzel text-lg font-bold flex items-center gap-2">{icon}{title}</h2>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}
